/* Bulk template upload — drag-and-drop multiple PDFs/PNGs/JPGs at once. */
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { isDealerUser } from "../lib/roles";

export default function AdminDocumentTemplatesBulk() {
  const { user, ready } = useAuth();
  const navigate = useNavigate();
  const [folders, setFolders] = useState([]);
  const [folder, setFolder] = useState("Custom Forms");
  const [files, setFiles] = useState([]);
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { if (ready && !isDealerUser(user)) navigate("/dealer-login"); }, [ready, user, navigate]);
  useEffect(() => {
    if (!isDealerUser(user)) return;
    api.get("/admin/documents/folders").then(r => {
      const list = r.data.folders || [];
      setFolders(list);
      if (list.length && !list.find(f => f.name === folder)) setFolder(list[0].name);
    });
    /* eslint-disable-next-line */
  }, [user]);

  const onPicked = (fl) => {
    const arr = Array.from(fl || []).filter(f => /\.(pdf|png|jpe?g)$/i.test(f.name));
    setFiles(prev => [...prev, ...arr]);
  };

  const onDrop = (e) => {
    e.preventDefault(); setDrag(false);
    onPicked(e.dataTransfer?.files);
  };

  const removeAt = (i) => setFiles(prev => prev.filter((_, idx) => idx !== i));

  const upload = async () => {
    if (!files.length) return;
    setBusy(true); setResults(null);
    try {
      const fd = new FormData();
      fd.append("folder", folder);
      files.forEach(f => fd.append("files", f));
      const r = await api.post("/admin/documents/templates/bulk", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setResults(r.data);
      setFiles([]);
      if (inputRef.current) inputRef.current.value = "";
    } catch (e) {
      alert("Bulk upload failed: " + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  return (
    <div className="ssc-container" style={{ paddingTop: 24, paddingBottom: 48 }} data-testid="bulk-upload">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <div>
          <Link to="/admin/documents/templates" className="text-xs text-blue-600 hover:underline">← Templates</Link>
          <h1 className="text-2xl font-bold text-[#061426] mt-1">Bulk Upload Templates</h1>
          <p className="text-sm text-gray-500">Drop or pick multiple forms at once. Each becomes a draft template — open it from the editor afterward to drop fields + map them.</p>
        </div>
      </div>

      <div className="bg-white border rounded-lg p-4 mb-4">
        <label className="block text-xs font-semibold text-gray-500 mb-1">Folder for all uploads</label>
        <select className="border border-gray-300 rounded p-2 text-sm mb-3 max-w-sm"
          value={folder} onChange={(e) => setFolder(e.target.value)} data-testid="bulk-folder">
          {folders.map(f => <option key={f.name} value={f.name}>{f.name}</option>)}
        </select>

        <div
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition ${drag ? "border-[#006DFF] bg-blue-50" : "border-gray-300 bg-gray-50 hover:bg-blue-50/30"}`}
          data-testid="bulk-dropzone"
        >
          <div className="text-3xl mb-1">📥</div>
          <div className="font-semibold text-[#061426]">Drop PDFs, JPGs, or PNGs here</div>
          <div className="text-xs text-gray-500 mt-1">…or click to pick them. Up to 25MB per file.</div>
          <input ref={inputRef} type="file" accept=".pdf,image/*" multiple
            onChange={(e) => onPicked(e.target.files)} className="hidden" data-testid="bulk-file-input" />
        </div>

        {files.length > 0 && (
          <>
            <ul className="divide-y mt-3" data-testid="bulk-queue">
              {files.map((f, i) => (
                <li key={i} className="py-1 flex items-center justify-between text-sm">
                  <span className="truncate flex-1">{f.name} <span className="text-xs text-gray-400 ml-2">({Math.round(f.size / 1024)} KB)</span></span>
                  <button className="text-xs text-red-600 hover:underline" onClick={() => removeAt(i)}>Remove</button>
                </li>
              ))}
            </ul>
            <div className="flex justify-end mt-3 gap-2">
              <button className="ssc-btn-secondary" onClick={() => setFiles([])} disabled={busy}>Clear</button>
              <button className="ssc-btn-primary" onClick={upload} disabled={busy} data-testid="bulk-upload-btn">
                {busy ? `Uploading ${files.length}…` : `Upload ${files.length} file${files.length === 1 ? "" : "s"}`}
              </button>
            </div>
          </>
        )}
      </div>

      {results && (
        <div className="bg-white border rounded-lg p-4" data-testid="bulk-results">
          <h3 className="font-bold text-sm mb-2">Results · {results.success}/{results.count} uploaded</h3>
          <ul className="divide-y">
            {(results.results || []).map((r, i) => (
              <li key={i} className="py-2 flex items-center justify-between gap-2 text-sm">
                <div className="truncate flex-1">
                  <span className={r.ok ? "text-emerald-700" : "text-red-700"}>{r.ok ? "✓" : "✗"}</span>{" "}
                  {r.filename}
                  {!r.ok && <span className="text-xs text-red-600 ml-2">{r.error}</span>}
                  {r.ok && r.template && <span className="text-xs text-gray-500 ml-2">{r.template.name} · {r.template.pages?.length || 0} page(s)</span>}
                </div>
                {r.ok && r.template && (
                  <Link to={`/admin/documents/templates/${r.template.id}/edit`}
                    className="text-xs text-blue-600 hover:underline shrink-0"
                    data-testid={`bulk-edit-${r.template.id}`}>Add fields →</Link>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
